import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useDeleteFolderMutation } from '@/features/api/endpoints/folderEndpoints';
import type { DirectusFolders } from '@/models/types';
import { getRelationId } from '@/shared/utils/file.utils';
import { ActionIcon, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconTrash } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import { removeSelectedItemId, setCurrentFolderId } from '../../../features/drive/driveSlice';

type DeleteFolderBtnProps = {
  folder: DirectusFolders;
};

const DeleteFolderBtn = ({ folder }: DeleteFolderBtnProps) => {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const currentFolderId = useAppSelector(state => state.drive.currentFolderId);
  const [deleteFolder] = useDeleteFolderMutation();

  const handleDeleteFolder = () => {
    modals.openConfirmModal({
      title: t('folder.delete.confirm'),
      children: <Text size="sm">{t('folder.delete.message')}</Text>,
      labels: {
        cancel: t('common.cancel'),
        confirm: t('common.delete'),
      },
      confirmProps: { color: 'red' },
      centered: true,
      onConfirm: async () => {
        try {
          await deleteFolder(folder.id).unwrap();
          dispatch(removeSelectedItemId(folder.id));
          if (currentFolderId === folder.id) {
            dispatch(setCurrentFolderId(folder.parent ? getRelationId(folder.parent) : null));
          }
          notifications.show({
            color: 'green',
            message: t('folder.delete.success'),
          });
        } catch {
          notifications.show({
            color: 'red',
            message: t('folder.delete.error'),
          });
        }
      },
    });
  };

  return (
    <Tooltip label={t('folder.delete.confirm')} withArrow>
      <ActionIcon
        variant="subtle"
        color="red"
        onClick={handleDeleteFolder}
        aria-label={t('folder.delete.confirm')}
      >
        <IconTrash size={18} />
      </ActionIcon>
    </Tooltip>
  );
};

export default DeleteFolderBtn;
